import { defineStore, acceptHMRUpdate } from 'pinia';
import { updateSetting } from '@/vuex/modules/user-settings/user-settings.utils';
import { useStarsStore } from '@/stores/stars.store';
import moment from '@/utils/moment-wrapper';
import { HISTORY_KEYS, MAX_TYPE_HISTORY, RECENTLY_SELECTED_HISTORY_TYPES } from '@/vuex/modules/user-settings/history/history.constants';
import { AI_AGENT_ICON_TYPE } from '@/ai-agents/components/ai-agents.constants.js';

import { useSubscriptionStore } from '@/stores/subscription.store';

interface AiAgent {
    id: string;
    name: string;
    group: string;
    appId: string;
    kind: string;
    dirty: boolean;
    isCoreEvent: boolean;
    icon?: { type: string };
}

export interface HistoryItem {
    id: string;
    timestamp: number;
}

interface HistoryState {
    map: Record<string, HistoryItem[]>;
}

function getInitialState (): HistoryState {
    return { map: {} };
}

export const useHistoryStore = defineStore('history', {
    state: getInitialState,
    getters: {
        itemHistory: (state) => (type: string): HistoryItem[] => {
            return state.map[type] || [];
        },
        allItemHistory: (state) => (types: string[] = []) => {
            return Object.keys(state.map)
                .filter((key) => {
                    if (!types.length) {
                        return true;
                    }

                    return types.includes(key);
                })
                .flatMap((key) => {
                    return state.map[key].map((item) => ({
                        ...item,
                        type: (HISTORY_KEYS as Record<string, string>)[key] || (RECENTLY_SELECTED_HISTORY_TYPES as Record<string, string>)[key],
                    }));
                })
                .sort((a, b) => b.timestamp - a.timestamp);
        },
        getRecentAgentEvents: (state) => (aiAgentsMap: Record<string, AiAgent>) => {
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { useAppsStore } = require('@/stores/apps.store');
            const appsStore = useAppsStore();

            return (state.map[RECENTLY_SELECTED_HISTORY_TYPES.AGENT_RECENTLY_SELECTED] || [])
                .filter(({ id }) => aiAgentsMap[id])
                .map(({ id, timestamp }) => {
                    const agent = aiAgentsMap[id];
                    const app = appsStore.getAppById(agent.appId);

                    return {
                        id: agent.id,
                        name: agent.name,
                        group: agent.group,
                        appId: agent.appId,
                        appIcon: appsStore.getIconByAppId(agent.appId),
                        appDisplayName: app ? app.displayName : undefined,
                        kind: agent.kind,
                        dirty: agent.dirty,
                        isCoreEvent: agent.isCoreEvent,
                        isStarred: !!useStarsStore().getStarByItemId(agent.id),
                        icon: agent.icon || { type: AI_AGENT_ICON_TYPE },
                        lastUsed: timestamp,
                    };
                });
        },
    },
    actions: {
        async updateMostRecent ({ id, type, maxHistory = MAX_TYPE_HISTORY }: { id: string; type: string; maxHistory?: number }) {
            if (!id) {
                return;
            }
            await this.updateBatchMostRecent({ ids: [id], type, maxHistory });
        },
        async updateBatchMostRecent ({ ids, type, maxHistory = MAX_TYPE_HISTORY }: { ids: string[]; type: string; maxHistory?: number }) {
            const { active: subscription } = useSubscriptionStore();
            const settingName = `${type}--${subscription.id}`;

            let itemHistory = this.itemHistory(type).filter((item) => !ids.includes(item.id));

            ids.forEach((id) => {
                if (id) {
                    itemHistory.unshift({
                        id,
                        timestamp: moment().valueOf(),
                    });
                }
            });

            if (itemHistory.length > maxHistory) {
                itemHistory = itemHistory.slice(0, maxHistory);
            }

            const updatedSetting = await updateSetting({
                name: settingName,
                value: JSON.stringify(itemHistory),
                userId: '{{uid}}',
            });

            this.map[type] = JSON.parse(updatedSetting.value);
        },
        setHistory ({ history }: { history: Record<string, HistoryItem[]> }) {
            this.map = history;
        },
        teardown () {
            Object.assign(this, getInitialState());
        },
    },
});

if (module.hot) { module.hot.accept(acceptHMRUpdate(useHistoryStore, module.hot)); }
